import { useEffect, useState, useMemo } from "react";
import api, { formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CategoriasAsignadas } from "@/components/CategoriasAsignadas";
import { Plus, Trash2, UserCog, KeyRound, Pencil, ShieldCheck, ShieldOff, AlertTriangle } from "lucide-react";

const ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "gerente_admin", label: "Gerente Administrador" },
  { value: "vendedor", label: "Vendedor" },
  { value: "fabrica", label: "Fábrica" },
  { value: "piloto", label: "Piloto" },
  { value: "facturacion", label: "Facturación" },
];

const emptyCreate = { email: "", password: "", name: "", role: "vendedor", activo: true };

export default function Usuarios() {
  const { user: currentUser } = useAuth();
  const canPurge = currentUser?.role === "gerente_admin" || currentUser?.role === "admin";
  const [items, setItems] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [defaultsPorRol, setDefaultsPorRol] = useState({});

  // Purge modal
  const [openPurge, setOpenPurge] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purging, setPurging] = useState(false);

  const runPurge = async () => {
    if (purgeConfirm !== "BORRAR TODO") return toast.error('Debes escribir exactamente: BORRAR TODO');
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-transactional", { confirm: "BORRAR TODO" });
      toast.success(`Limpieza completa. ${data.total} registros borrados.`);
      setOpenPurge(false); setPurgeConfirm("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPurging(false); }
  };

  // Create modal
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(emptyCreate);

  // Edit modal
  const [editing, setEditing] = useState(null); // user object
  const [editForm, setEditForm] = useState({ email: "", name: "", role: "vendedor", activo: true, permisos_modulos: null });

  // Password modal
  const [pwdUser, setPwdUser] = useState(null);
  const [pwdVal, setPwdVal] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadModulos = async () => {
    try {
      const { data } = await api.get("/modulos");
      setModulos(data.modulos || []);
      setDefaultsPorRol(data.defaults_por_rol || {});
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); loadModulos(); }, []);

  // ------ Create ------
  const save = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error("Nombre, email y contraseña son obligatorios");
      return;
    }
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setOpenCreate(false); setForm(emptyCreate); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ------ Edit ------
  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      email: u.email,
      name: u.name,
      role: u.role,
      activo: u.activo !== false,
      // Si permisos_modulos es null/undefined => usar defaults por rol
      permisos_modulos: Array.isArray(u.permisos_modulos) ? [...u.permisos_modulos] : null,
      categorias_asignadas: Array.isArray(u.categorias_asignadas) ? [...u.categorias_asignadas] : [],
    });
  };
  const closeEdit = () => { setEditing(null); };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        email: editForm.email,
        name: editForm.name,
        role: editForm.role,
        activo: editForm.activo,
        permisos_modulos: editForm.permisos_modulos,
        categorias_asignadas: editForm.categorias_asignadas || [],
      };
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("Usuario actualizado");
      closeEdit(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ------ Password ------
  const openPwd = (u) => { setPwdUser(u); setPwdVal(""); };
  const savePwd = async () => {
    if (!pwdUser) return;
    if (!pwdVal || pwdVal.length < 4) { toast.error("Mínimo 4 caracteres"); return; }
    try {
      await api.post(`/users/${pwdUser.id}/password`, { password: pwdVal });
      toast.success("Contraseña actualizada");
      setPwdUser(null); setPwdVal("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ------ Toggle Activo inline ------
  const toggleActivo = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { activo: !(u.activo !== false) });
      toast.success((u.activo !== false) ? "Usuario desactivado" : "Usuario activado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ------ Delete ------
  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar usuario ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ------ Permissions helpers ------
  const currentPermisos = useMemo(() => {
    if (Array.isArray(editForm.permisos_modulos)) return editForm.permisos_modulos;
    return defaultsPorRol[editForm.role] || [];
  }, [editForm.permisos_modulos, editForm.role, defaultsPorRol]);

  const isModuloOn = (key) => currentPermisos.includes(key);
  const toggleModulo = (key) => {
    // Al primer toque, si estaba en null (default por rol), lo materializamos
    const base = Array.isArray(editForm.permisos_modulos)
      ? [...editForm.permisos_modulos]
      : [...(defaultsPorRol[editForm.role] || [])];
    const next = base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
    setEditForm({ ...editForm, permisos_modulos: next });
  };
  const resetToRolDefault = () => setEditForm({ ...editForm, permisos_modulos: null });
  const enableAll = () => setEditForm({ ...editForm, permisos_modulos: modulos.map((m) => m.key) });
  const disableAll = () => setEditForm({ ...editForm, permisos_modulos: [] });

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Sistema</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <UserCog className="w-8 h-8" /> Usuarios
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">Gestioná cuentas, roles, contraseñas y permisos por módulo.</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="new-user-btn">
              <Plus className="w-4 h-4" /> Nuevo usuario
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo usuario</DialogTitle>
              <DialogDescription>Los permisos de módulos por defecto los define el rol. Podés personalizarlos después de crear el usuario.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div><Label>Contraseña</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
              <div>
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2">
                <div>
                  <div className="text-sm font-semibold">Cuenta activa</div>
                  <div className="text-[11px] text-zinc-500">Si está desactivada, no podrá iniciar sesión.</div>
                </div>
                <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} data-testid="user-activo" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancelar</Button>
              <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-user-btn">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Nombre</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Email</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Rol</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Estado</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Permisos</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Creado</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center text-zinc-400 py-10">Sin usuarios.</td></tr>
              )}
              {items.map((u) => {
                const activo = u.activo !== false;
                const perm = Array.isArray(u.permisos_modulos) ? u.permisos_modulos : null;
                const permText = perm === null
                  ? <span className="text-zinc-500 text-xs">Por defecto ({(defaultsPorRol[u.role] || []).length})</span>
                  : <span className="text-orange-700 text-xs font-semibold">Personalizado ({perm.length})</span>;
                return (
                  <tr key={u.id} className="hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3 font-semibold">{u.name}</td>
                    <td className="px-5 py-3 text-zinc-600">{u.email}</td>
                    <td className="px-5 py-3">
                      <span className="text-[10px] uppercase tracking-widest font-semibold bg-zinc-900 text-white px-2 py-0.5 rounded">
                        {ROLES.find((r) => r.value === u.role)?.label || u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => toggleActivo(u)}
                        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${activo ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}
                        data-testid={`toggle-activo-${u.id}`}
                      >
                        {activo ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                        {activo ? "Activo" : "Inactivo"}
                      </button>
                    </td>
                    <td className="px-5 py-3">{permText}</td>
                    <td className="px-5 py-3 text-zinc-500 text-xs">{formatDate(u.created_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="ghost" title="Editar" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Cambiar contraseña" onClick={() => openPwd(u)} data-testid={`pwd-user-${u.id}`}>
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" title="Eliminar" onClick={() => remove(u)} data-testid={`del-user-${u.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Nombre</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" /></div>
            <div><Label>Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" /></div>
            <div>
              <Label>Rol</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="flex items-center justify-between w-full rounded-md border border-zinc-200 px-3 py-2">
                <div>
                  <div className="text-sm font-semibold">Cuenta activa</div>
                  <div className="text-[11px] text-zinc-500">Si está desactivada, no podrá iniciar sesión.</div>
                </div>
                <Switch checked={editForm.activo} onCheckedChange={(v) => setEditForm({ ...editForm, activo: v })} data-testid="edit-user-activo" />
              </div>
            </div>
          </div>

          {editForm.role === "vendedor" && (
            <CategoriasAsignadas value={editForm.categorias_asignadas} onChange={(v) => setEditForm({ ...editForm, categorias_asignadas: v })} />
          )}

          <div className="mt-2 border-t border-zinc-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Permisos por módulo</div>
                <div className="text-[11px] text-zinc-500">
                  {editForm.permisos_modulos === null
                    ? <>Usando <b>valores por defecto del rol</b> ({(defaultsPorRol[editForm.role] || []).length} módulos).</>
                    : <>Permisos <b>personalizados</b> ({currentPermisos.length} activos).</>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={resetToRolDefault} data-testid="reset-permisos">Usar defecto</Button>
                <Button size="sm" variant="outline" onClick={enableAll} data-testid="enable-all-permisos">Habilitar todo</Button>
                <Button size="sm" variant="outline" onClick={disableAll} data-testid="disable-all-permisos">Bloquear todo</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
              {modulos.map((m) => {
                const on = isModuloOn(m.key);
                return (
                  <label
                    key={m.key}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${on ? "bg-emerald-50 border-emerald-200" : "bg-zinc-50 border-zinc-200"}`}
                    data-testid={`permiso-${m.key}`}
                  >
                    <span className={on ? "text-emerald-800 font-semibold" : "text-zinc-500"}>{m.label}</span>
                    <Switch checked={on} onCheckedChange={() => toggleModulo(m.key)} />
                  </label>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeEdit}>Cancelar</Button>
            <Button onClick={saveEdit} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-edit-user">Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password modal */}
      <Dialog open={!!pwdUser} onOpenChange={(v) => !v && setPwdUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar contraseña</DialogTitle>
            <DialogDescription>{pwdUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Nueva contraseña</Label>
            <Input type="password" value={pwdVal} onChange={(e) => setPwdVal(e.target.value)} autoFocus data-testid="new-password-input" />
            <p className="text-[11px] text-zinc-500">Mínimo 4 caracteres. El usuario deberá usar la nueva contraseña la próxima vez.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdUser(null)}>Cancelar</Button>
            <Button onClick={savePwd} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-password-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zona peligrosa: solo Gerente Administrador */}
      {canPurge && (
        <Card className="border-red-300 border-2 p-5 mt-8" data-testid="zona-peligrosa">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-bold text-red-800 text-lg">Zona peligrosa · Reinicio del sistema</h3>
              <p className="text-sm text-zinc-600 mt-1">
                Borra <b>todos los datos transaccionales</b> del sistema: pedidos, gastos, movimientos de caja, transferencias, depósitos de plataforma de guías, cierres de ruta, compras de inventario y auditoría.
              </p>
              <p className="text-sm text-zinc-600 mt-1">
                <b>Se preservan:</b> productos, clientes, cajas, bancos, categorías, usuarios, transportistas y modificadores.
              </p>
              <p className="text-xs text-red-700 font-semibold mt-2">
                ⚠️ Esta acción NO se puede deshacer. El correlativo de pedidos se reiniciará a PED-A1.
              </p>
              <Button
                onClick={() => setOpenPurge(true)}
                className="mt-4 bg-red-600 hover:bg-red-700 text-white"
                data-testid="btn-purge-open"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Reiniciar datos transaccionales
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Dialog open={openPurge} onOpenChange={(v) => { setOpenPurge(v); if (!v) setPurgeConfirm(""); }}>
        <DialogContent className="max-w-md" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Confirmar reinicio</DialogTitle>
            <DialogDescription>
              Vas a borrar TODOS los pedidos, gastos, movimientos de caja, transferencias, depósitos y cierres de ruta. Los catálogos (productos, clientes, cajas, bancos, usuarios) se conservan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">Para confirmar, escribí exactamente: <b className="text-red-700">BORRAR TODO</b></Label>
            <Input
              value={purgeConfirm}
              onChange={(e) => setPurgeConfirm(e.target.value)}
              placeholder="BORRAR TODO"
              data-testid="purge-confirm-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPurge(false)} disabled={purging}>Cancelar</Button>
            <Button
              onClick={runPurge}
              disabled={purgeConfirm !== "BORRAR TODO" || purging}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="purge-confirm-btn"
            >
              {purging ? "Borrando…" : "Sí, borrar todo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
